import type { ColDef } from "ag-grid-enterprise";

import {
  DETAILS_TYPES,
  extractCustomFieldId,
  isCustomField,
} from "@web-app/pages/settings/FormBuilderTab/constants";
import type {
  DetailsType,
  OrderFormField,
} from "@web-app/pages/settings/FormBuilderTab/types";

export const CUSTOM_FIELD_COL_ID_PREFIX = "custom_field_";

export const getColumnKey = (colDef: ColDef): string =>
  colDef.colId ?? colDef.field ?? colDef.headerName ?? "";

type FieldColumnMapping = [
  detailsType: DetailsType,
  fieldKey: string,
  columnKeys: string[],
];

const REQUIREMENT_COLUMN_KEYS = [
  "dropoffRequirement.signature",
  "dropoffRequirement.photo",
  "dropoffRequirement.note",
];

const FIELD_COLUMN_MAPPINGS: FieldColumnMapping[] = [
  ["pickup", "systemField_pickupName", ["pickup.name"]],
  ["pickup", "systemField_pickupPhone", ["pickup.phone"]],
  ["pickup", "systemField_pickupAddress", ["pickup.address"]],
  ["pickup", "systemField_pickupBusinessName", ["pickup.businessName"]],
  ["pickup", "systemField_pickupNote", ["pickup.note"]],
  ["pickup", "systemField_pickupServiceTime", ["details.serviceDuration"]],
  ["pickup", "systemField_scheduledStartDateTime", ["scheduledStartDateTime"]],
  ["pickup", "systemField_pickupEndTime", ["pickupWindow"]],
  ["pickup", "systemField_fullName", ["pickup.name"]],
  ["pickup", "systemField_phone", ["pickup.phone"]],
  ["pickup", "systemField_address", ["pickup.address"]],
  ["pickup", "systemField_businessName", ["pickup.businessName"]],
  ["pickup", "systemField_note", ["pickup.note"]],
  ["pickup", "systemField_serviceTime", ["details.serviceDuration"]],
  ["pickup", "systemField_pickupAt", ["pickupWindow"]],
  ["dropoff", "systemField_dropOffName", ["dropoff.name"]],
  ["dropoff", "systemField_dropOffPhone", ["dropoff.phone"]],
  ["dropoff", "systemField_dropoffAddress", ["dropoff.address"]],
  ["dropoff", "systemField_dropOffBusinessName", ["dropoff.businessName"]],
  ["dropoff", "systemField_dropOffNote", ["dropoff.note"]],
  ["dropoff", "systemField_dropOffServiceTime", ["details.serviceDuration"]],
  ["dropoff", "systemField_dropoffOrderRequirements", REQUIREMENT_COLUMN_KEYS],
  ["dropoff", "systemField_scheduledEndDateTime", ["scheduledEndDateTime"]],
  ["dropoff", "systemField_dropOffStartTime", ["dropoffWindow"]],
  ["dropoff", "systemField_fullName", ["dropoff.name"]],
  ["dropoff", "systemField_phone", ["dropoff.phone"]],
  ["dropoff", "systemField_address", ["dropoff.address"]],
  ["dropoff", "systemField_businessName", ["dropoff.businessName"]],
  ["dropoff", "systemField_note", ["dropoff.note"]],
  ["dropoff", "systemField_serviceTime", ["details.serviceDuration"]],
  ["dropoff", "systemField_orderRequirements", REQUIREMENT_COLUMN_KEYS],
  ["dropoff", "systemField_dropoffAt", ["dropoffWindow"]],

  ["order", "systemField_description", ["details.description"]],
  ["order", "systemField_timezone", ["timezone"]],
  ["order", "systemField_externalId", ["externalId"]],
  ["order", "systemField_vehicleType", ["minVehicle"]],
  ["order", "systemField_value", ["details.value"]],
  ["order", "systemField_tip", ["details.tip"]],
  ["order", "systemField_itemsCount", ["details.itemsCount"]],
  ["order", "systemField_length", ["details.length"]],
  ["order", "systemField_width", ["details.width"]],
  ["order", "systemField_height", ["details.height"]],
  ["order", "systemField_weight", ["details.weight"]],
  ["order", "systemField_priority", ["priority"]],
  ["order", "systemField_connectedWorkspaceId", ["connections"]],
];

const FIELD_KEY_TO_COLUMN_KEYS = new Map<string, string[]>(
  FIELD_COLUMN_MAPPINGS.map(([detailsType, fieldKey, columnKeys]) => [
    `${detailsType}:${fieldKey}`,
    columnKeys,
  ]),
);

const resolveColumnKeys = (field: OrderFormField): string[] => {
  if (isCustomField(field.fieldKey)) {
    return [
      `${CUSTOM_FIELD_COL_ID_PREFIX}${extractCustomFieldId(field.fieldKey)}`,
    ];
  }
  return (
    FIELD_KEY_TO_COLUMN_KEYS.get(`${field.detailsType}:${field.fieldKey}`) ?? []
  );
};

export const getConfiguredColumnKeys = (
  formFields: OrderFormField[],
): string[] => {
  const sorted = [...formFields].sort((a, b) => {
    const sectionDelta =
      DETAILS_TYPES.indexOf(a.detailsType) -
      DETAILS_TYPES.indexOf(b.detailsType);
    return sectionDelta !== 0 ? sectionDelta : a.position - b.position;
  });

  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const field of sorted) {
    for (const columnKey of resolveColumnKeys(field)) {
      if (!seen.has(columnKey)) {
        seen.add(columnKey);
        ordered.push(columnKey);
      }
    }
  }
  return ordered;
};

export const applyFormConfigColumnOrder = (
  columnDefs: ColDef[],
  formFields?: OrderFormField[],
): ColDef[] => {
  if (!formFields?.length) return columnDefs;

  const configOrder = getConfiguredColumnKeys(formFields);
  if (configOrder.length === 0) return columnDefs;

  const rank = new Map(configOrder.map((key, index) => [key, index]));
  const managedIndexes: number[] = [];
  columnDefs.forEach((colDef, index) => {
    if (rank.has(getColumnKey(colDef))) managedIndexes.push(index);
  });

  if (managedIndexes.length < 2) return columnDefs;

  const managed = managedIndexes
    .map((index) => columnDefs[index])
    .sort(
      (a, b) =>
        (rank.get(getColumnKey(a)) ?? 0) - (rank.get(getColumnKey(b)) ?? 0),
    );

  const insertAt = managedIndexes[0];
  const managedIndexSet = new Set(managedIndexes);
  const result: ColDef[] = [];
  columnDefs.forEach((colDef, index) => {
    if (index === insertAt) result.push(...managed);
    if (!managedIndexSet.has(index)) result.push(colDef);
  });
  return result;
};
